import { ContextMenuHandler, IMenuItem } from "@asup/context-menu";
import React from "react";
import { REACT_APP_STAGE } from "../../../config";
import { useArsTable } from "./ArsTableContextProvider";
import { cellActionConfig } from "./cellActionConfig";
import { ArsTableCellData } from "./interface";

export interface ArsTableCellContextMenuProviderProps<T> {
  children: React.ReactNode;
  rowId: string;
  colId: string;
  value: ArsTableCellData<T>;
}

/**
 * Wraps one ArsTable cell editor with context-menu actions chosen from the scaffold cell type.
 *
 * @param props - Component props
 * @param props.children - Cell editor content
 * @param props.rowId - Scaffold row id for the current cell
 * @param props.colId - Scaffold column id for the current cell
 * @param props.value - Current scaffold cell value and metadata
 * @returns Context-menu wrapper for one ArsTable cell
 */
export const ArsTableCellContextMenuProvider = <T,>({
  children,
  rowId,
  colId,
  value,
}: ArsTableCellContextMenuProviderProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsTable<T>();

  const actionList: IMenuItem[] = cellActionConfig(value, state)
    .contextMenuActions.map((config) => ({
      label: config.label,
      action: () => {
        dispatch({
          operation: config.operation,
          cellValue: value,
        });
      },
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  /** Add developer info display */
  // istanbul ignore next
  const developerMenuItems: IMenuItem[] = [
    "local",
    "development",
    "test",
    "__REACT_APP_STAGE__",
  ].includes(REACT_APP_STAGE)
    ? [
        {
          label: "Developer info",
          group: [
            ...Object.entries(value)
              .filter(([key]) => key !== "displayContent")
              .map(([key, val]) => ({
                label: (
                  <div>
                    <span style={{ fontWeight: "bold" }}>{`${key}`}</span>
                    {`: ${val}`}
                  </div>
                ),
              })),
          ],
        },
      ]
    : [];
  actionList.push(...developerMenuItems);

  return (
    <ContextMenuHandler
      className="w-100 h-100 d-block"
      menuItems={actionList}
      key={`${rowId}-${colId}-${value.cellType}`}
    >
      <div style={{ width: "100%", height: "100%" }}>{children}</div>
    </ContextMenuHandler>
  );
};

ArsTableCellContextMenuProvider.displayName = "ArsTableCellContextMenuProvider";
